// ===========================================
// AttachmentsField.jsx
// Componente universal de adjuntos — MAPASAC ERP v2026
// Configuración 100% desde JSON (emailConfig.adjuntos)
// Zero hardcode
// ===========================================

import React, { useRef } from 'react';
import { Paperclip, X, FileText, Code, Table, Image, Lock } from 'lucide-react';

// ===========================================
// Mapa de íconos Lucide desde string JSON
// ===========================================
const ICON_MAP = {
  'file-text': FileText,
  'code':      Code,
  'table':     Table,
  'image':     Image,
};

const getIconComponent = (iconName) => ICON_MAP[iconName] || FileText;

// ===========================================
// AttachmentsField
// Props:
//   value        — array de adjuntos actuales [{ label, tipo, base64, esDefault }]
//   onChange     — callback al cambiar lista
//   adjuntosConfig — emailConfig.adjuntos desde BD
//   disabled     — readonly
// ===========================================
const AttachmentsField = ({ value = [], onChange, adjuntosConfig = {}, disabled = false, labelStyles = {}, inputStyles = {} }) => {

  const fileInputRef = useRef(null);

  const permitidos   = adjuntosConfig.permitidos   || ['pdf', 'xml', 'xls', 'xlsx', 'jpg', 'png'];
  const maxSizeMb    = adjuntosConfig.max_size_mb   || 5;
  const maxArchivos  = adjuntosConfig.max_archivos  || 5;
  const iconos       = adjuntosConfig.iconos        || {};

  // ===========================================
  // Obtener extensión del archivo
  // ===========================================
  const getExtension = (filename) => filename.split('.').pop().toLowerCase();

  // ===========================================
  // Obtener ícono y color desde JSON
  // ===========================================
  const getIconConfig = (tipo) => {
    const cfg = iconos[tipo] || { icon: 'file-text', color: '#6b7280' };
    return {
      Icon:  getIconComponent(cfg.icon),
      color: cfg.color
    };
  };

  // ===========================================
  // Click en botón clip — abre selector
  // ===========================================
  const handleClipClick = () => {
    if (disabled) return;
    if (value.length >= maxArchivos) {
      alert(`Máximo ${maxArchivos} archivos permitidos`);
      return;
    }
    fileInputRef.current?.click();
  };

  // ===========================================
  // Procesar archivos seleccionados
  // ===========================================
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const nuevos = [];

    for (const file of files) {
      const ext = getExtension(file.name);

      // Validar tipo permitido
      if (!permitidos.includes(ext)) {
        alert(`Tipo de archivo no permitido: .${ext}\nPermitidos: ${permitidos.join(', ')}`);
        continue;
      }

      // Validar tamaño
      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > maxSizeMb) {
        alert(`Archivo demasiado grande: ${file.name}\nMáximo permitido: ${maxSizeMb} MB`);
        continue;
      }

      // Verificar que no exceda max_archivos
      if (value.length + nuevos.length >= maxArchivos) {
        alert(`Máximo ${maxArchivos} archivos permitidos`);
        break;
      }

      // Convertir a Base64
      const base64 = await fileToBase64(file);
      nuevos.push({
        label:     file.name,
        tipo:      ext,
        base64,
        esDefault: false,
        sizeMb:    sizeMb.toFixed(2)
      });
    }

    if (nuevos.length > 0) {
      onChange?.([...value, ...nuevos]);
    }

    // Reset input para permitir seleccionar el mismo archivo de nuevo
    e.target.value = '';
  };

  // ===========================================
  // Convertir File a Base64
  // ===========================================
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // ===========================================
  // Eliminar archivo (solo los no default)
  // ===========================================
  const handleRemove = (index) => {
    const updated = value.filter((_, i) => i !== index);
    onChange?.(updated);
  };

  // ===========================================
  // RENDER
  // ===========================================
  return (
    <div style={{ width: '100%' }}>

      {/* Label */}
      <div
        style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          marginBottom:   labelStyles.marginBottom || '0.5rem'
        }}
      >
        <label
          style={{
            fontSize:   labelStyles.fontSize   || '0.875rem',
            fontWeight: labelStyles.fontWeight || '500',
            color:      labelStyles.textColor  || '#374151'
          }}
        >
          Adjuntar
        </label>

        {/* Botón clip */}
        {!disabled && (
          <button
            type="button"
            onClick={handleClipClick}
            title={`Adjuntar archivo (${permitidos.join(', ')})`}
            style={{
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'center',
              width:           '28px',
              height:          '28px',
              borderRadius:    '6px',
              border:          '1px solid #d1d5db',
              backgroundColor: '#ffffff',
              color:           '#6b7280',
              cursor:          value.length >= maxArchivos ? 'not-allowed' : 'pointer',
              opacity:         value.length >= maxArchivos ? 0.5 : 1,
              transition:      'all 150ms ease'
            }}
            onMouseEnter={e => {
              if (value.length < maxArchivos) {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.color = '#111827';
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            <Paperclip size={14} />
          </button>
        )}
      </div>

      {/* Input file oculto */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={permitidos.map(ext => `.${ext}`).join(',')}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Lista de adjuntos */}
      <div
        style={{
          border:       `1px solid ${inputStyles.borderColor || '#d1d5db'}`,
          borderRadius: inputStyles.borderRadius || '6px',
          backgroundColor: '#ffffff',
          overflow:     'hidden',
          minHeight:    '36px'
        }}
      >
        {value.length === 0 ? (
          <div
            style={{
              padding:    '0.5rem 0.75rem',
              fontSize:   inputStyles.fontSize || '0.75rem',
              color:      '#9ca3af',
              fontStyle:  'italic'
            }}
          >
            Sin archivos adjuntos
          </div>
        ) : (
          value.map((archivo, index) => {
            const { Icon, color } = getIconConfig(archivo.tipo);
            return (
              <div
                key={index}
                style={{
                  display:         'flex',
                  alignItems:      'center',
                  padding:         '0.375rem 0.75rem',
                  borderBottom:    index < value.length - 1 ? '1px solid #f3f4f6' : 'none',
                  backgroundColor: archivo.esDefault ? '#f8fafc' : '#ffffff',
                  gap:             '0.5rem'
                }}
              >
                {/* Ícono tipo archivo */}
                <Icon size={14} style={{ color, flexShrink: 0 }} />

                {/* Nombre archivo */}
                <span
                  style={{
                    flex:       1,
                    fontSize:   inputStyles.fontSize || '0.75rem',
                    color:      '#374151',
                    overflow:   'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {archivo.label}
                </span>

                {/* Tamaño — solo archivos del usuario */}
                {!archivo.esDefault && archivo.sizeMb && (
                  <span style={{ fontSize: '0.65rem', color: '#9ca3af', flexShrink: 0 }}>
                    {archivo.sizeMb} MB
                  </span>
                )}

                {/* Badge default — no borrable */}
                {archivo.esDefault ? (
                  <Lock size={12} style={{ color: '#9ca3af', flexShrink: 0 }} title="Adjunto predeterminado" />
                ) : (
                  !disabled && (
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      style={{
                        display:         'flex',
                        alignItems:      'center',
                        justifyContent:  'center',
                        width:           '18px',
                        height:          '18px',
                        borderRadius:    '4px',
                        border:          'none',
                        backgroundColor: 'transparent',
                        color:           '#9ca3af',
                        cursor:          'pointer',
                        flexShrink:      0,
                        padding:         0
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = '#fee2e2';
                        e.currentTarget.style.color = '#dc2626';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#9ca3af';
                      }}
                      title="Eliminar adjunto"
                    >
                      <X size={12} />
                    </button>
                  )
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Contador */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
        <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>
          {value.length}/{maxArchivos} archivos
        </span>
      </div>

    </div>
  );
};

export default AttachmentsField;
